import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  InfiniteLoader,
  ManagementListToolbar,
  PaginatedList,
  RecordFormDialog,
  RecordTable,
  StatusPill,
  formatRecordDate,
} from "@taunggyi-plus/design-system";

type Row = { id: string; [key: string]: unknown };
const text = (r: Row, k: string) => String(r[k] ?? "");
const amount = (v: unknown) => `${(Number(v ?? 0) / 100).toLocaleString()} MMK`;
export function useWorkspaceData<T>(name: string, args: Record<string, unknown> = {}) {
  const { client, user } = useAuth();
  const [data, setData] = useState<T | null>(null),
    [error, setError] = useState(""),
    [revision, refresh] = useState(0);
  const key = JSON.stringify(args);
  useEffect(() => {
    if (!client || !user) return;
    let active = true;
    void client.rpc(name, JSON.parse(key)).then((r) => {
      if (!active) return;
      if (r.error) setError(r.error.message);
      else {
        setData(r.data as T);
        setError("");
      }
    });
    return () => {
      active = false;
    };
  }, [client, user, name, key, revision]);
  return { data, error, refresh: () => refresh((n) => n + 1) };
}
export function CountStrip({ values }: { values: readonly [string, ReactNode][] }) {
  return (
    <div className="workspace-counts">
      {values.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}
export function WorkspaceDashboard({ appId }: { appId: string }) {
  const [tab, setTab] = useState("Current Status");
  const q = useWorkspaceData<Record<string, unknown>>("workspace_overview", { target_app: appId });
  const d = q.data;
  return (
    <section className="workspace-dashboard">
      <div className="workspace-tabs" role="tablist" aria-label="Dashboard views">
        {["Current Status", "Income Status", "Need to Do"].map((t) => (
          <button
            key={t}
            role="tab"
            id={`tab-${t.replaceAll(" ", "-")}`}
            aria-controls="dashboard-panel"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {q.error && (
        <p role="alert">
          {q.error} <button onClick={q.refresh}>Retry</button>
        </p>
      )}
      {!d && !q.error && <p role="status">Loading dashboard…</p>}
      {d && (
        <div
          role="tabpanel"
          id="dashboard-panel"
          aria-labelledby={`tab-${tab.replaceAll(" ", "-")}`}
        >
          {tab === "Current Status" && (
            <>
              <div className="workspace-metrics">
                <Card>
                  <span>
                    {appId === "delivery-rider" ? "Assigned delivery fleets" : "Live providers"}
                  </span>
                  <strong>{Number(d.active_providers)}</strong>
                  <small>{Number(d.providers)} total</small>
                </Card>
                <Card>
                  <span>Waiting for a response</span>
                  <strong>{Number(d.waiting)}</strong>
                  <a href="/orders">View orders →</a>
                </Card>
                <Card>
                  <span>Ongoing service work</span>
                  <strong>{Number(d.ongoing)}</strong>
                  <small>{Number(d.total_orders)} total orders</small>
                </Card>
              </div>
              <Card>
                <h2>Category status</h2>
                <div className="workspace-counts">
                  {(d.categories as { service: string; total: number }[]).map((c) => (
                    <div key={c.service}>
                      <span>{c.service.replaceAll("_", " ")}</span>
                      <strong>{c.total}</strong>
                    </div>
                  ))}
                </div>
                {!(d.categories as unknown[]).length && <p>No service activity yet.</p>}
              </Card>
            </>
          )}
          {tab === "Income Status" && (
            <>
              <CountStrip
                values={[
                  [
                    appId === "delivery-rider" ? "Cash collected today" : "Today income",
                    amount(d.today_income),
                  ],
                  [
                    appId === "delivery-rider" ? "Total cash collected" : "Total income",
                    amount(d.total_income),
                  ],
                ]}
              />
              <div className="workspace-metrics">
                <Card>
                  <span>
                    {appId === "admin"
                      ? "Platform income"
                      : appId === "delivery-rider"
                        ? "Collected cash"
                        : "Service income"}
                  </span>
                  <strong>{amount(d.total_income)}</strong>
                </Card>
                <Card>
                  <span>Event income</span>
                  <strong>{amount(d.event_income)}</strong>
                </Card>
              </div>
              <a className="button" href="/finance">
                View finance
              </a>
            </>
          )}
          {tab === "Need to Do" && (
            <>
              <CountStrip
                values={[
                  ["Waiting", Number(d.waiting)],
                  ["Current orders", Number(d.current_orders)],
                ]}
              />
              <PaginatedList
                items={(d.todo ?? []) as Row[]}
                renderItem={(r) => (
                  <Card key={r.id}>
                    <h3>{text(r, "title")}</h3>
                    <StatusPill>{text(r, "status")}</StatusPill>
                    <p>{text(r, "detail")}</p>
                    <a className="button" href={`/orders?order=${r.id}`}>
                      Open order
                    </a>
                  </Card>
                )}
              />
              {!(d.todo as unknown[]).length && <Card>No pending actions. You’re up to date.</Card>}
            </>
          )}
        </div>
      )}
    </section>
  );
}
export function WorkspaceOrderCounts({ appId }: { appId: string }) {
  const q = useWorkspaceData<Record<string, unknown>>("workspace_overview", { target_app: appId });
  return q.data ? (
    <CountStrip
      values={[
        ["Current orders", Number(q.data.current_orders)],
        ["Total orders", Number(q.data.total_orders)],
      ]}
    />
  ) : null;
}

export function TaxonomySettings({ provider = false }: { provider?: boolean }) {
  const { client, user } = useAuth();
  const [terms, setTerms] = useState<Row[]>([]),
    [orgs, setOrgs] = useState<Row[]>([]),
    [assignments, setAssignments] = useState<{ organization_id: string; term_id: string }[]>([]),
    [revision, setRevision] = useState(0),
    [message, setMessage] = useState(""),
    [query, setQuery] = useState(""),
    [kind, setKind] = useState("all"),
    [editing, setEditing] = useState<Row | null | undefined>();
  useEffect(() => {
    if (!client || !user) return;
    let active = true;
    void Promise.all([
      client.from("catalog_terms").select("*"),
      client.from("organizations").select("id,name,kind"),
      client.from("catalog_assignments").select("*"),
      client
        .from("organization_memberships")
        .select("organization_id,role")
        .eq("user_id", user.id)
        .eq("status", "active"),
    ]).then(([t, o, a, m]) => {
      if (!active) return;
      const e = [t, o, a, m].find((r) => r.error)?.error;
      if (e) {
        setMessage(e.message);
        return;
      }
      setTerms(t.data ?? []);
      setOrgs(
        (o.data ?? []).filter(
          (r: Row) =>
            !provider ||
            (m.data ?? []).some(
              (v: { organization_id: string; role: string }) =>
                v.organization_id === r.id &&
                ["hotel_owner", "merchant_owner", "delivery_owner", "taxi_group_leader"].includes(
                  v.role,
                ),
            ),
        ),
      );
      setAssignments(a.data ?? []);
    });
    return () => {
      active = false;
    };
  }, [client, user, provider, revision]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client) return;
    const f = new FormData(e.currentTarget);
    const r = await client.rpc("save_catalog_term", {
      target_id: editing?.id ?? null,
      term_kind: String(f.get("kind")),
      target_service: String(f.get("service")),
      label: String(f.get("name")),
      active: f.get("active") === "on",
    });
    setMessage(r.error?.message ?? "Saved.");
    if (!r.error) {
      setEditing(undefined);
      setRevision((n) => n + 1);
    }
  }
  const options = [
    { value: "all", label: "All" },
    ...["category", "tag"].map((value) => ({ value, label: value })),
  ];
  return (
    <section>
      <p role="status">{message}</p>
      {provider ? (
        <>
          {orgs.map((org) => (
            <Card key={org.id}>
              <h2>{text(org, "name")}</h2>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const terms = new FormData(e.currentTarget).getAll("term");
                  const r = await client!.rpc("assign_catalog_terms", { org: org.id, terms });
                  setMessage(r.error?.message ?? "Categories and tags saved.");
                  if (!r.error) setRevision((n) => n + 1);
                }}
              >
                <div className="term-options">
                  {terms
                    .filter((t) => t.is_active && (t.service === "all" || t.service === org.kind))
                    .map((t) => (
                      <label key={t.id}>
                        <input
                          type="checkbox"
                          name="term"
                          value={t.id}
                          defaultChecked={assignments.some(
                            (a) => a.organization_id === org.id && a.term_id === t.id,
                          )}
                        />
                        {text(t, "name")} <small>{text(t, "kind")}</small>
                      </label>
                    ))}
                </div>
                <button className="button">Save selection</button>
              </form>
            </Card>
          ))}
        </>
      ) : (
        <>
          <ManagementListToolbar
            query={query}
            onQueryChange={setQuery}
            searchLabel="Search categories and tags"
            resultCount={terms.length}
            filters={[{ label: "Type", value: kind, onChange: setKind, options }]}
            onClear={() => {
              setQuery("");
              setKind("all");
            }}
            actions={
              <button
                className="toolbar-icon-button"
                aria-label="Add category or tag"
                onClick={() => setEditing(null)}
              >
                +
              </button>
            }
          />
          <RecordTable
            items={terms.filter(
              (t) =>
                (kind === "all" || t.kind === kind) &&
                text(t, "name").toLowerCase().includes(query.toLowerCase()),
            )}
            rowKey={(r) => r.id}
            columns={[
              { key: "name", label: "Name", render: (r) => text(r, "name") },
              { key: "kind", label: "Type", render: (r) => text(r, "kind") },
              { key: "service", label: "Service", render: (r) => text(r, "service") },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusPill>{r.is_active ? "active" : "inactive"}</StatusPill>,
              },
              {
                key: "action",
                label: "Actions",
                render: (r) => (
                  <button className="button button--secondary" onClick={() => setEditing(r)}>
                    Edit
                  </button>
                ),
              },
            ]}
          />
          {editing !== undefined && (
            <RecordFormDialog
              title={editing ? "Edit category or tag" : "Add category or tag"}
              open
              hideTrigger
              onClose={() => setEditing(undefined)}
            >
              <form onSubmit={save} className="workspace-form">
                <label>
                  Name
                  <input
                    required
                    maxLength={80}
                    name="name"
                    defaultValue={editing ? text(editing, "name") : ""}
                  />
                </label>
                <label>
                  Type
                  <select name="kind" defaultValue={editing ? text(editing, "kind") : "category"}>
                    <option value="category">Category</option>
                    <option value="tag">Tag</option>
                  </select>
                </label>
                <label>
                  Service
                  <select name="service" defaultValue={editing ? text(editing, "service") : "all"}>
                    {["all", "hotel", "merchant", "delivery_fleet", "taxi_group"].map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    defaultChecked={editing ? Boolean(editing.is_active) : true}
                  />
                  Active
                </label>
                <button className="button">Save</button>
              </form>
            </RecordFormDialog>
          )}
        </>
      )}
    </section>
  );
}

export function ManagedContent({
  section,
  locale = "en",
  fallback,
}: {
  section: string;
  locale?: string;
  fallback?: ReactNode;
}) {
  const q = useWorkspaceData<{ title: string; body: string }>("read_app_content", {
    target_section: section,
    target_locale: locale,
  });
  if (q.error) return <p role="alert">{q.error}</p>;
  return q.data ? (
    <article className="content-document">
      <h2>{q.data.title}</h2>
      <div style={{ whiteSpace: "pre-wrap" }}>{q.data.body}</div>
    </article>
  ) : (
    <>{fallback ?? <p>Information will be published here by Admin.</p>}</>
  );
}
export function ContentSettings({ section }: { section: string }) {
  const { client } = useAuth();
  const [locale, setLocale] = useState("en"),
    [message, setMessage] = useState("");
  const q = useWorkspaceData<{ title: string; body: string }>("read_app_content", {
    target_section: section,
    target_locale: locale,
  });
  return (
    <section>
      <label>
        Language
        <select value={locale} onChange={(e) => setLocale(e.target.value)}>
          {["en", "my", "shn", "zh"].map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
      </label>
      <p role="status">{message || q.error}</p>
      <RecordFormDialog title="Edit published content">
        <form
          key={`${locale}:${q.data?.body}`}
          className="workspace-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            const r = await client!.rpc("save_app_content", {
              target_section: section,
              target_locale: locale,
              target_title: f.get("title"),
              target_body: f.get("body"),
            });
            setMessage(r.error?.message ?? "Published locally.");
            if (!r.error) {
              form.closest("dialog")?.close();
              q.refresh();
            }
          }}
        >
          <label>
            Title
            <input name="title" required maxLength={120} defaultValue={q.data?.title ?? ""} />
          </label>
          <label>
            Content
            <textarea
              name="body"
              required
              maxLength={30000}
              rows={14}
              defaultValue={q.data?.body ?? ""}
            />
          </label>
          <button className="button">Save content</button>
        </form>
      </RecordFormDialog>
      <ManagedContent key={`${locale}:${q.data?.body}`} section={section} locale={locale} />
    </section>
  );
}

export function ManagedRiders({ admin = false }: { admin?: boolean }) {
  const { client } = useAuth();
  const q = useWorkspaceData<{ fleets: Row[]; riders: Row[] }>("delivery_rider_workspace");
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState("all"),
    [message, setMessage] = useState(""),
    [editing, setEditing] = useState<Row | null | undefined>(),
    [limitOrg, setLimitOrg] = useState<Row | null>(null),
    [busy, setBusy] = useState(false);
  const fleets = q.data?.fleets ?? [],
    riders = q.data?.riders ?? [];
  const filtered = riders.filter(
    (r) =>
      (status === "all" || (status === "active") === Boolean(r.is_active)) &&
      `${r.display_name} ${r.phone} ${r.username}`.toLowerCase().includes(query.toLowerCase()),
  );
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client || busy) return;
    setBusy(true);
    try {
      const f = new FormData(e.currentTarget);
      const r = await client.rpc("save_delivery_rider_account", {
        target_id: editing?.id ?? null,
        fleet: f.get("fleet"),
        display_name: f.get("name"),
        username: f.get("username") ?? "",
        phone: f.get("phone"),
        email: f.get("email") ?? "",
        initial_password: f.get("password") ?? "",
        vehicle: f.get("vehicle"),
        active: f.get("active") === "on",
      });
      setMessage(r.error?.message ?? "Rider account saved.");
      if (!r.error) {
        setEditing(undefined);
        q.refresh();
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section>
      <CountStrip
        values={[
          ["Active riders", riders.filter((r) => r.is_active).length],
          ["Total rider accounts", riders.length],
        ]}
      />
      <p role="status">{message || q.error}</p>
      <div className="rider-capacity">
        {fleets.map((f) => (
          <div key={f.id}>
            <strong>{text(f, "name")}</strong>
            <span>
              {Number(f.rider_count)} / {Number(f.rider_limit)} rider accounts
            </span>
            {admin ? (
              <button className="button button--secondary" onClick={() => setLimitOrg(f)}>
                Change limit
              </button>
            ) : (
              <small>Covered by owner subscription · Admin manages the account limit</small>
            )}
          </div>
        ))}
      </div>
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search name, phone or username"
        resultCount={filtered.length}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: [
              { value: "all", label: "All riders" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ],
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
        }}
        actions={
          <button
            className="toolbar-icon-button"
            aria-label="Add rider"
            disabled={!fleets.some((f) => Number(f.rider_count) < Number(f.rider_limit))}
            onClick={() => setEditing(null)}
          >
            +
          </button>
        }
      />
      <RecordTable
        items={filtered}
        rowKey={(r) => r.id}
        columns={[
          {
            key: "name",
            label: "Rider",
            render: (r) => (
              <>
                <strong>{text(r, "display_name")}</strong>
                <small>{text(r, "username")}</small>
              </>
            ),
          },
          { key: "phone", label: "Phone", render: (r) => text(r, "phone") },
          { key: "vehicle", label: "Vehicle", render: (r) => text(r, "vehicle_label") },
          {
            key: "status",
            label: "Status",
            render: (r) => <StatusPill>{r.is_active ? "active" : "inactive"}</StatusPill>,
          },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <>
                <button className="button button--secondary" onClick={() => setEditing(r)}>
                  Edit account
                </button>
                <a className="button button--secondary" href={`/orders?rider=${r.id}`}>
                  Assign orders
                </a>
              </>
            ),
          },
        ]}
      />
      {editing !== undefined && (
        <RecordFormDialog
          title={editing ? "Edit rider account" : "Add rider account"}
          open
          hideTrigger
          onClose={() => setEditing(undefined)}
        >
          <form className="workspace-form" onSubmit={save}>
            <label>
              Fleet
              <select
                name="fleet"
                defaultValue={editing ? text(editing, "fleet_id") : fleets[0]?.id}
              >
                {fleets
                  .filter((f) => !editing || f.id === editing.fleet_id)
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {text(f, "name")}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Name
              <input
                name="name"
                required
                defaultValue={editing ? text(editing, "display_name") : ""}
              />
            </label>
            {!editing && (
              <>
                <label>
                  Username
                  <input name="username" required pattern="[a-zA-Z0-9_]{3,40}" />
                </label>
                <label>
                  Email
                  <input name="email" type="email" required />
                </label>
              </>
            )}
            <label>
              Phone
              <input name="phone" required defaultValue={editing ? text(editing, "phone") : ""} />
            </label>
            <label>
              Vehicle
              <input name="vehicle" defaultValue={editing ? text(editing, "vehicle_label") : ""} />
            </label>
            {(!editing || editing.can_manage_account || admin) && (
              <label>
                {editing ? "New password (optional)" : "Initial password"}
                <input
                  name="password"
                  type="password"
                  minLength={12}
                  required={!editing}
                  autoComplete="new-password"
                />
              </label>
            )}
            <label>
              <input
                type="checkbox"
                name="active"
                defaultChecked={editing ? Boolean(editing.is_active) : true}
              />
              Active account
            </label>
            <button className="button" disabled={busy}>
              {busy ? "Saving…" : "Save rider"}
            </button>
          </form>
        </RecordFormDialog>
      )}
      {limitOrg && (
        <RecordFormDialog
          title="Rider account limit"
          open
          hideTrigger
          onClose={() => setLimitOrg(null)}
        >
          <form
            className="workspace-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const limit = Number(new FormData(e.currentTarget).get("limit"));
              const r = await client!.rpc("set_delivery_rider_limit", {
                org: limitOrg.organization_id,
                new_limit: limit,
              });
              setMessage(r.error?.message ?? "Limit updated.");
              if (!r.error) {
                setLimitOrg(null);
                q.refresh();
              }
            }}
          >
            <label>
              Maximum rider accounts
              <input
                name="limit"
                type="number"
                min={Number(limitOrg.rider_count)}
                max={10000}
                required
                defaultValue={Number(limitOrg.rider_limit)}
              />
            </label>
            <button className="button">Save limit</button>
          </form>
        </RecordFormDialog>
      )}
    </section>
  );
}

export function AdminDirectory({
  customers = false,
  actions,
  revision = 0,
}: {
  customers?: boolean;
  actions?: ReactNode;
  revision?: number;
}) {
  const [query, setQuery] = useState(""),
    [category, setCategory] = useState("all"),
    [status, setStatus] = useState("all"),
    [page, setPage] = useState(0),
    [records, setRecords] = useState<Row[]>([]);
  const key = JSON.stringify([query, category, status, customers, revision]);
  const { client } = useAuth();
  const [result, setResult] = useState<{
    key: string;
    page: number;
    total: number;
    active?: number;
    error: string;
  }>({ key: "", page: -1, total: 0, error: "" });
  const currentPage = result.key === key ? page : 0;
  const loading = result.key !== key || result.page !== currentPage;
  useEffect(() => {
    if (!client) return;
    let active = true;
    void client
      .rpc(
        customers ? "admin_customers_page" : "partners_page",
        customers
          ? { query, page: currentPage }
          : { query, category, active_only: status === "active", page: currentPage },
      )
      .then(async (r) => {
        if (!active) return;
        const data = r.data as { items: Row[]; total: number; active?: number } | null;
        const selected = new URLSearchParams(window.location.search).get("record");
        if (
          !customers &&
          data &&
          selected &&
          /^[0-9a-f-]{36}$/i.test(selected) &&
          !data.items.some((r) => r.id === selected)
        ) {
          const detail = await client
            .from("organizations")
            .select("id,name,kind,is_active,created_at")
            .eq("id", selected)
            .maybeSingle();
          if (!active) return;
          if (!detail.error && detail.data) data.items = [detail.data as Row, ...data.items];
        }
        if (!r.error && data)
          setRecords((old) =>
            currentPage === 0
              ? data.items
              : [...old.filter((o) => !data.items.some((n) => n.id === o.id)), ...data.items],
          );
        setResult({
          key,
          page: currentPage,
          total: data?.total ?? 0,
          active: data?.active,
          error: r.error?.message ?? "",
        });
      });
    return () => {
      active = false;
    };
  }, [client, key, currentPage, query, category, status, customers]);
  const q = { data: result, error: result.error };
  function resetFilter(update: () => void) {
    update();
    setPage(0);
  }
  const selectedId = new URLSearchParams(window.location.search).get("record");
  const serviceName = (kind: string) =>
    ({
      hotel: "Hotel",
      merchant: "Restaurant / Merchant",
      delivery_fleet: "Delivery owner",
      taxi_group: "Taxi driver",
    })[kind] ?? kind.replaceAll("_", " ");
  if (selectedId && !customers) {
    const partner = records.find((record) => record.id === selectedId);
    return (
      <article className="partner-detail">
        <a className="more-back-link" href="/partners">
          ← Back to partners
        </a>
        {q.error ? (
          <p role="alert">{q.error}</p>
        ) : loading ? (
          <p role="status">Loading partner…</p>
        ) : !partner ? (
          <p role="status">Partner not found. Return to partners to choose another record.</p>
        ) : (
          <>
            <header className="partner-detail__header">
              <div>
                <p className="partner-detail__eyebrow">{serviceName(text(partner, "kind"))}</p>
                <h2>{text(partner, "name")}</h2>
              </div>
              <StatusPill tone={partner.is_active ? "success" : "warning"}>
                {partner.is_active ? "Active" : "Inactive"}
              </StatusPill>
            </header>
            <div className="partner-detail__grid">
              <Card className="partner-detail__panel">
                <h3>Partner information</h3>
                <dl>
                  <div>
                    <dt>Service</dt>
                    <dd>{serviceName(text(partner, "kind"))}</dd>
                  </div>
                  <div>
                    <dt>Joined</dt>
                    <dd>{formatRecordDate(text(partner, "created_at"))}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{partner.is_active ? "Active" : "Inactive"}</dd>
                  </div>
                </dl>
              </Card>
              <Card className="partner-detail__panel">
                <h3>Manage partner</h3>
                <p>Update service details or manage subscription coverage.</p>
                <div className="partner-detail__actions">
                  <a className="button" href={`/more/services?partner=${partner.id}`}>
                    Manage provider
                  </a>
                  <a
                    className="button button--secondary"
                    href={`/more/subscriptions?partner=${partner.id}`}
                  >
                    Subscription
                  </a>
                  {partner.kind === "delivery_fleet" && (
                    <a className="button button--secondary" href="/more/delivery-riders">
                      Riders & account limit
                    </a>
                  )}
                </div>
              </Card>
            </div>
          </>
        )}
      </article>
    );
  }
  return (
    <section>
      <CountStrip
        values={
          customers
            ? [["Customers", q.data?.total ?? 0]]
            : [
                ["Active partners", q.data?.active ?? 0],
                ["Total partners", q.data?.total ?? 0],
              ]
        }
      />
      <ManagementListToolbar
        actions={actions}
        query={query}
        onQueryChange={(v) => resetFilter(() => setQuery(v))}
        searchLabel={customers ? "Search customers" : "Search partners"}
        resultCount={q.data?.total ?? 0}
        filters={
          customers
            ? []
            : [
                {
                  label: "Service",
                  value: category,
                  onChange: (v: string) => resetFilter(() => setCategory(v)),
                  options: ["all", "hotel", "merchant", "delivery_fleet", "taxi_group"].map(
                    (value) => ({
                      value,
                      label: value === "all" ? "All services" : serviceName(value),
                    }),
                  ),
                },
                {
                  label: "Status",
                  value: status,
                  onChange: (v: string) => resetFilter(() => setStatus(v)),
                  options: [
                    { value: "all", label: "All" },
                    { value: "active", label: "Active" },
                  ],
                },
              ]
        }
        onClear={() => {
          setQuery("");
          setCategory("all");
          setStatus("all");
        }}
      />
      <p role="alert">{q.error}</p>
      <PaginatedList
        pagination="external"
        items={records}
        resetKey={key}
        renderItem={(r) => (
          <Card key={r.id}>
            <h3>{text(r, customers ? "display_name" : "name")}</h3>
            <StatusPill>{customers ? "customer" : r.is_active ? "active" : "inactive"}</StatusPill>
            <p>{customers ? text(r, "email") : serviceName(text(r, "kind"))}</p>
            <p>{formatRecordDate(text(r, "created_at"))}</p>
            {!customers && (
              <>
                <a className="button" href={`/more/services?partner=${r.id}`}>
                  Manage provider
                </a>
                <a
                  className="button button--secondary"
                  href={`/more/subscriptions?partner=${r.id}`}
                >
                  Subscription
                </a>
                {r.kind === "delivery_fleet" && (
                  <a className="button button--secondary" href="/more/delivery-riders">
                    Riders & account limit
                  </a>
                )}
              </>
            )}
          </Card>
        )}
      />
      {!new URLSearchParams(window.location.search).has("record") && (
        <InfiniteLoader
          loading={loading}
          hasMore={!q.error && records.length < result.total}
          onLoad={() => setPage(currentPage + 1)}
          label={`${records.length} of ${result.total}`}
        />
      )}
    </section>
  );
}

export function AdminAnnouncements() {
  const { client } = useAuth();
  const [role, setRole] = useState("customer"),
    [preview, setPreview] = useState<{ title: string; body: string; id: string } | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const q = useWorkspaceData<number>("admin_announcement_preview", { target_role: role });
  return (
    <section>
      <p>
        Create an announcement for one account role. Review the recipients and message before
        sending.
      </p>
      <RecordFormDialog title="Create notification">
        <form
          className="workspace-form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            setPreview({
              title: String(f.get("title")),
              body: String(f.get("body")),
              id: crypto.randomUUID(),
            });
          }}
        >
          <label>
            Recipients
            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setPreview(null);
              }}
            >
              {[
                "customer",
                "hotel_owner",
                "merchant_owner",
                "delivery_owner",
                "delivery_rider",
                "taxi_driver",
              ].map((r) => (
                <option key={r} value={r}>
                  {r.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </label>
          <p>{q.data ?? 0} recipients</p>
          <label>
            Title
            <input name="title" required maxLength={160} />
          </label>
          <label>
            Message
            <textarea name="body" required maxLength={1000} />
          </label>
          <button className="button" disabled={!q.data}>
            Preview notification
          </button>
          {preview && (
            <Card>
              <h3>{preview.title}</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>{preview.body}</p>
              <p>
                {q.data} {role.replaceAll("_", " ")} accounts
              </p>
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const r = await client!.rpc("send_admin_announcement", {
                    request_id: preview.id,
                    target_role: role,
                    target_title: preview.title,
                    target_body: preview.body,
                  });
                  setMessage(r.error?.message ?? `Notification sent to ${r.data} accounts.`);
                  setBusy(false);
                  if (!r.error) setPreview(null);
                }}
              >
                Send this notification
              </button>
            </Card>
          )}
          <p role="status">{message || q.error}</p>
        </form>
      </RecordFormDialog>
    </section>
  );
}

export function CustomerSavedRecords({ reviews = false }: { reviews?: boolean }) {
  const { client, user } = useAuth();
  const favourites = useWorkspaceData<Row[]>("my_favourite_providers");
  const [rows, setRows] = useState<Row[]>([]),
    [query, setQuery] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    if (!reviews || !client || !user) return;
    let active = true;
    void client.rpc("my_service_reviews").then((r) => {
      if (!active) return;
      setRows((r.data as Row[] | null) ?? []);
      setError(r.error?.message ?? "");
    });
    return () => {
      active = false;
    };
  }, [client, user, reviews]);
  const items = (reviews ? rows : (favourites.data ?? [])).filter((r) =>
    Object.values(r).join(" ").toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <section>
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel={reviews ? "Search reviews" : "Search favourites"}
        resultCount={items.length}
        onClear={() => setQuery("")}
      />
      <p role="alert">{error || favourites.error}</p>
      <PaginatedList
        items={items}
        label={reviews ? "My reviews" : "Favourite providers"}
        renderItem={(r) => (
          <Card key={r.id}>
            <h3>{reviews ? `${r.rating} / 5` : text(r, "name")}</h3>
            <p>{reviews ? text(r, "comment") : text(r, "kind").replaceAll("_", " ")}</p>
            {reviews ? (
              <a className="button" href={`/orders?list=Order+pages&record=${r.order_id}`}>
                View order
              </a>
            ) : (
              <>
                <a
                  className="button"
                  href={`/services?type=${({ hotel: "hotel", merchant: "food", delivery_fleet: "delivery", taxi_group: "taxi" } as Record<string, string>)[text(r, "kind")]}`}
                >
                  Explore service
                </a>
                <button
                  className="button button--secondary"
                  onClick={async () => {
                    const result = await client!.rpc("set_provider_favourite", {
                      org: r.id,
                      favourite: false,
                    });
                    setError(result.error?.message ?? "");
                    if (!result.error) favourites.refresh();
                  }}
                >
                  Remove favourite
                </button>
              </>
            )}
          </Card>
        )}
      />
    </section>
  );
}
